import * as amplitude from '@amplitude/analytics-browser';

interface EventConfig {
  appVersion: string;
  appStateVersion: string;
  amplitudeApiKey: string;
}

interface EventUserProps {
  userID?: string;
  gender?: string;
  email?: string;
  name?: string;
  grade?: string;
  type?: string;
  aaid?: string;
  phone?: string;
  activeSession: string;
  deviceID: string;
  profileLoginDetailsId: string;
}

interface EventCommonProps {
  project?: 'geneo-test' | 'geneo-stage' | 'geneo-prod';
  appVersion: string;
  appStateVersion: string;
  platform: string;
  urlPath: string;
  buildType: string;
}

interface UserProps {
  gender?: string;
  email?: string;
  name?: string;
  grade?: string;
  type?: string;
  aaid?: string;
  phone?: string;
  platform: string;
  buildType: string;
  userID?: string;
}

interface Event {
  type: string;
  props: Record<string, any>;
}

const getPlatform = () => {
  return 'web'; //Others to be added as per identifier
};

const getBuildType = () => {
  return process.env.NODE_ENV || 'development'; //Others to be added as per identifier
};

export const getCommonProperties = (config: EventConfig): EventCommonProps => {
  return {
    appVersion: config.appVersion,
    appStateVersion: config.appStateVersion,
    platform: getPlatform(),
    urlPath: window.location.pathname,
    buildType: getBuildType(),
  };
};

export const getUserProperties = (userProps: EventUserProps): UserProps => {
  return {
    ...userProps,
    platform: getPlatform(),
    buildType: getBuildType(),
  };
};

const aaidValidate = (userAAID: string): boolean => {
  const aaidPresented = [8, 4, 4, 4, 12];
  const splitAaid = userAAID.split('-');
  return (
    aaidPresented.length === splitAaid.length &&
    aaidPresented.every((value, index) => value === splitAaid[index].length)
  );
};

export const initializeAmplitude = (apiKey: string) => {
  amplitude.init(apiKey, {
    // defaultTracking: {
    //   sessions: false,
    //   pageViews: false,
    //   formInteractions: false,
    //   fileDownloads: false,
    // },
    autocapture: {
      attribution: false,
      pageViews: false,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: {
        cssSelectorAllowlist: ['.MuiButton-root', 'a', 'button', 'select'],
        pageUrlAllowlist: [/.*/],
      },
    },
  });
};

const formatUserId = (userId: string | undefined): string => {
  if (!userId) return '';
  // Remove any non-alphanumeric characters and ensure length is valid
  const cleanId = userId.replace(/[^a-zA-Z0-9]/g, '');
  // Amplitude requires user_id to be between 5 and 140 characters
  if (cleanId.length < 5) {
    return cleanId.padEnd(5, '0');
  }
  return cleanId.slice(0, 140);
};
function formatUserId2(userID: string | undefined): string {
  if (!userID) return '';
  return String(userID).padStart(5, '0');
}

export const sendAmplitudeEvent = (
  event: Event,
  config: EventConfig,
  userProps: EventUserProps,
  userProperties: UserProps,
  commonProperties: EventCommonProps
) => {
  const { type, props } = event;
  const data = { ...commonProperties, ...props };

  // Only set user ID for login success event
  if (event.type === 'login_success' && userProps.userID) {
    // const formattedUserId = `000${userProps.userID}`;
    const formattedUserId = formatUserId2(userProps.userID);
    amplitude.setUserId(formattedUserId);
  }

  // Set AAID as device ID if valid
  if (userProps.aaid && aaidValidate(userProps.aaid)) {
    const formattedDeviceId = userProps.aaid;
    amplitude.setDeviceId(formattedDeviceId);
  }

  const identify = new amplitude.Identify();

  // Only set user properties after successful login
  if (event.type === 'login_success') {
    // Properties that should only be set once (demographic/signup data)
    if (userProperties.gender)
      identify.setOnce('gender', userProperties.gender);
    if (userProperties.grade) identify.setOnce('grade', userProperties.grade);
    if (userProperties.type) identify.setOnce('type', userProperties.type);

    // Properties that can be updated
    if (userProperties.email) identify.set('email', userProperties.email);
    if (userProperties.name) identify.set('name', userProperties.name);
    if (userProperties.phone) identify.set('phone', userProperties.phone);
    identify.set('platform', userProperties.platform);
    identify.set('buildType', userProperties.buildType);

    // Send identify call before tracking event
    amplitude.identify(identify);
  }

  amplitude.track(type, data);
};

export const sendEvent = (
  event: Event,
  config: EventConfig,
  userProps: EventUserProps
) => {
  const commonProperties = getCommonProperties(config);
  const userProperties = getUserProperties(userProps);

  sendAmplitudeEvent(
    event,
    config,
    userProps,
    userProperties,
    commonProperties
  );
};

// Reset user - useful for logout
export const resetUser = () => {
  amplitude.reset();
};
